import React from "react";
import { Box, Typography, Paper, Stack } from "@mui/material";

import StatCard from "../components/StatCard";

import RevenueLineChart from "../components/charts/RevenueLineChart";
import TicketsBarChart from "../components/charts/TicketsBarChart";
import CategoryDonutChart from "../components/charts/CategoryDonutChart";

import InsightsPanel from "../components/InsightsPanel";
import ComparisonCard from "../components/ComparisonCard";
import RiskEventsCard from "../components/RiskEventsCard";
import OrganizersRankingCard from "../components/OrganizersRankingCard";

import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";

/** Card padrão do dashboard (fundo limpo + borderRadius 1) */
function DashCard({ children, sx }) {
  return (
    <Paper
      sx={{
        p: { xs: 2.2, md: 2.6 },
        borderRadius: 1,
        height: "100%",
        minHeight: { xs: 260, md: 320 },
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

/** span responsivo: xs=1col, md=2col, lg=5col */
const span = (xs = 1, md = 1, lg = 1) => ({
  gridColumn: { xs: `span ${xs}`, md: `span ${md}`, lg: `span ${lg}` },
});

export default function Dashboard() {
  return (
    <Box
      sx={{
        display: "grid",
        gap: { xs: 2, md: 2.5 },
        gridTemplateColumns: {
          xs: "1fr",
          md: "repeat(2, 1fr)",
          lg: "repeat(5, 1fr)",
        },
        alignItems: "start",
      }}
    >
      {/* HEADER */}
      <Box sx={span(1, 2, 5)}>
        <Typography fontSize={{ xs: 22, md: 26 }} fontWeight={900}>
          Visão Geral da Plataforma
        </Typography>
        <Typography color="text.secondary">
          Crescimento, performance e inteligência do ecossistema ONURB
        </Typography>
      </Box>

      {/* KPIs (4) */}
      <Box sx={span(1, 1, 1)}>
        <StatCard
          icon={<PaidRoundedIcon />}
          label="Receita do mês"
          value="R$ 248.430"
          hint="+18% vs mês anterior"
        />
      </Box>

      <Box sx={span(1, 1, 1)}>
        <StatCard
          icon={<EventAvailableRoundedIcon />}
          label="Eventos ativos"
          value="42"
          hint="12 vendendo hoje"
        />
      </Box>

      <Box sx={span(1, 1, 1)}>
        <StatCard
          icon={<GroupsRoundedIcon />}
          label="Organizadores"
          value="186"
          hint="+7 esta semana"
        />
      </Box>

      <Box sx={span(1, 1, 2)}>
        <StatCard
          icon={<ConfirmationNumberRoundedIcon />}
          label="Ingressos vendidos"
          value="38.912"
          hint="Ticket médio R$ 63,80"
        />
      </Box>

      {/* GRÁFICOS */}
      <Box sx={span(1, 2, 2)}>
        <DashCard>
          <Stack spacing={0.3} mb={1.2}>
            <Typography fontWeight={900}>Receita ao longo do tempo</Typography>
            <Typography fontSize={13} color="text.secondary">
              Visão diária consolidada da plataforma
            </Typography>
          </Stack>
          <RevenueLineChart />
        </DashCard>
      </Box>

      <Box sx={span(1, 2, 2)}>
        <DashCard>
          <Stack spacing={0.3} mb={1.2}>
            <Typography fontWeight={900}>Ingressos vendidos</Typography>
            <Typography fontSize={13} color="text.secondary">
              Últimos 7 dias
            </Typography>
          </Stack>
          <TicketsBarChart />
        </DashCard>
      </Box>

      {/* COMPARATIVO (fica à direita no desktop) */}
      <Box sx={span(1, 2, 1)}>
        {/* Garanta que ComparisonCard também esteja com borderRadius 1 */}
        <ComparisonCard />
      </Box>

      {/* INSIGHTS (bem largo no desktop) */}
      <Box sx={span(1, 2, 3)}>
        <DashCard>
          <InsightsPanel />
        </DashCard>
      </Box>

      {/* DONUT */}
      <Box sx={span(1, 2, 2)}>
        <DashCard>
          <Typography fontWeight={900} mb={1.2}>
            Distribuição por categoria
          </Typography>
          <CategoryDonutChart />
        </DashCard>
      </Box>

      {/* RANKING (usa o componente pronto) */}
      <Box sx={span(1, 2, 2)}>
        <OrganizersRankingCard />
      </Box>

      {/* TOP EVENTOS (span 3 pra ficar protagonista) */}
      <Box sx={span(1, 2, 3)}>
        <DashCard>
          <Typography fontWeight={900} mb={1.2}>
            Eventos com maior volume de vendas
          </Typography>

          {[
            { name: "Festival Aurora", pct: 96 },
            { name: "Sunset Beats", pct: 87 },
            { name: "Tech Summit BH", pct: 96 },
            { name: "Réveillon Serra", pct: 64 },
          ].map((e, i) => (
            <Box key={i} sx={{ mb: 1.6 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography fontWeight={700}>{e.name}</Typography>
                <Typography color="text.secondary">{e.pct}%</Typography>
              </Stack>

              {/* Mantém radius 1 */}
              <Paper
                variant="outlined"
                sx={{
                  mt: 0.8,
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: 10,
                    width: `${e.pct}%`,
                    bgcolor: "primary.main",
                  }}
                />
              </Paper>
            </Box>
          ))}
        </DashCard>
      </Box>

      {/* EVENTOS EM RISCO (usa o componente pronto) */}
      <Box sx={span(1, 2, 2)}>
        <RiskEventsCard />
      </Box>

      {/* BLOCO FUTURO */}
      <Box sx={span(1, 2, 2)}>
        <DashCard>
          <Typography fontWeight={900}>Bloco futuro</Typography>
          <Typography color="text.secondary" fontSize={13} mt={0.5}>
            Reservado para expansão (financeiro, antifraude, suporte, etc.)
          </Typography>
        </DashCard>
      </Box>
    </Box>
  );
}
